/**
 * Regla única de "qué lista de estados usa un embarque".
 *
 * Cada compañía elige UN campo base (SHIPMENT_STATUS.statusBaseField):
 *   - ausente, vacío o 'transportMode' → la lista de su modo (statusByMode) → el legado (status, solo AIR/SEA).
 *   - otro campo ('operationTypeId' o un select de systemFields) → la lista de la opción del embarque
 *     (statusSets con field = ese campo y value = la opción). Una opción sin lista, o con todos sus
 *     estados ocultos, usa la de su modo → el legado (estados por defecto).
 * Las listas de statusSets de otro campo se conservan inactivas: no se resuelven ni entran a los
 * catálogos, pero se leen por clave ('set:<id>') y son el último recurso al buscar un estado o su etiqueta.
 *
 * COPIA IDÉNTICA, byte a byte, en:
 *   - front: src/utils/shipmentStatusSets.ts
 *   - CF:    functions/src/status-sets/resolve-status-list.ts
 *            (ramas feat/status-sets, feat/tracking-status-sets, feat/bigquery-status-sets,
 *             feat/calculated-fields-status-sets)
 * Casos de prueba compartidos: status-sets.cases.ts (también idéntico en todas las copias).
 * No se edita una copia sin las demás. Sin imports a propósito.
 */

export type TransportModeKey = 'AIR' | 'SEA' | 'ROA';

/** Forma mínima de un estado. Cada llamador pasa su propio tipo (T) con más campos. */
export interface StatusListItem {
  id: string;
  label?: string;
  order?: number;
  visible?: boolean;
}

/** Lista de estados de UNA opción de un campo base. */
export interface StatusSetEntry<T extends StatusListItem = StatusListItem> {
  id: string;
  /** Etiqueta de la opción al guardar la lista; si falta, el valor. */
  label: string;
  /** Ruta del campo en el doc del embarque ('operationTypeId', 'miCampo', 'milestones.x'). */
  field: string;
  /** Valor guardado de la opción (id del tipo de operación, value de la opción del select). */
  value: string;
  /** Tal como están guardados (sin filtrar por visible ni ordenar). */
  statuses: T[];
}

export interface ResolvedStatusList<T extends StatusListItem = StatusListItem> {
  /** 'set:<id>' para la lista de una opción; 'AIR' | 'SEA' | 'ROA' para modo o legado. */
  key: string;
  source: 'set' | 'mode' | 'legacy';
  setId: string | null;
  modeKey: TransportModeKey;
  /** Solo visibles, ordenados por order. */
  statuses: T[];
}

export const STATUS_SET_KEY_PREFIX = 'set:';

/** Campo base por defecto: el modo de transporte (listas de statusByMode). */
export const DEFAULT_STATUS_BASE_FIELD = 'transportMode';

const MODE_KEYS: TransportModeKey[] = ['AIR', 'SEA', 'ROA'];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function orderOf(item: StatusListItem): number {
  return typeof item.order === 'number' ? item.order : 0;
}

function visibleSorted<T extends StatusListItem>(list: unknown): T[] {
  if (!Array.isArray(list)) return [];
  const result: T[] = [];
  for (const item of list) {
    const record = asRecord(item);
    if (record && typeof record.id === 'string' && record.visible !== false) result.push(item as T);
  }
  return result.sort((a, b) => orderOf(a) - orderOf(b));
}

/** Igual que mapTransportModeToKey de onStatusUpdate: vacío o desconocido → 'SEA'. */
export function mapTransportModeToKey(transportMode: unknown): TransportModeKey {
  const normalized = typeof transportMode === 'string' ? transportMode.toLowerCase() : '';
  if (
    normalized.includes('ocean') ||
    normalized.includes('sea') ||
    normalized.includes('marítimo') ||
    normalized.includes('maritimo')
  ) {
    return 'SEA';
  }
  if (normalized.includes('air') || normalized.includes('aéreo') || normalized.includes('aereo')) {
    return 'AIR';
  }
  if (
    normalized.includes('otm') ||
    normalized.includes('road') ||
    normalized.includes('terrestre') ||
    normalized.includes('truck')
  ) {
    return 'ROA';
  }
  return 'SEA';
}

/** string, number o boolean → trim + minúsculas; cualquier otra cosa → ''. */
export function normalizeStatusSetValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim().toLowerCase();
  }
  return '';
}

/** Lee una ruta con puntos ('milestones.eta'); undefined si algún nivel no es objeto. */
export function readFieldPath(entity: unknown, path: string): unknown {
  let current: unknown = entity;
  for (const key of path.split('.')) {
    const record = asRecord(current);
    if (!record) return undefined;
    current = record[key];
  }
  return current;
}

/** Campo que define los estados: statusBaseField si es un string no vacío; si no, 'transportMode'. */
export function getStatusBaseField(config: unknown): string {
  const record = asRecord(config);
  const raw = record ? record.statusBaseField : undefined;
  return typeof raw === 'string' && raw.trim() !== '' ? raw : DEFAULT_STATUS_BASE_FIELD;
}

/** Todas las listas válidas de statusSets (field y value no vacíos), activas o no, ordenadas por id. */
export function getStatusSetEntries<T extends StatusListItem = StatusListItem>(config: unknown): StatusSetEntry<T>[] {
  const configRecord = asRecord(config);
  const sets = asRecord(configRecord ? configRecord.statusSets : undefined);
  if (!sets) return [];
  const entries: StatusSetEntry<T>[] = [];
  for (const id of Object.keys(sets)) {
    const raw = asRecord(sets[id]);
    if (!raw) continue;
    const field = raw.field;
    const value = raw.value;
    if (typeof field !== 'string' || field.trim() === '') continue;
    if (typeof value !== 'string' || value.trim() === '') continue;
    const label = raw.label;
    entries.push({
      id,
      label: typeof label === 'string' && label.trim() !== '' ? label : value,
      field,
      value,
      statuses: Array.isArray(raw.statuses) ? (raw.statuses as T[]) : [],
    });
  }
  return entries.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Listas del campo base activo, ordenadas por id; [] si el campo base es el modo de transporte. */
export function getActiveStatusSetEntries<T extends StatusListItem = StatusListItem>(config: unknown): StatusSetEntry<T>[] {
  const baseField = getStatusBaseField(config);
  if (baseField === DEFAULT_STATUS_BASE_FIELD) return [];
  const result: StatusSetEntry<T>[] = [];
  for (const entry of getStatusSetEntries<T>(config)) {
    if (entry.field === baseField) result.push(entry);
  }
  return result;
}

/**
 * Lista activa de la opción del registro: la primera (por id) del campo base cuyo valor coincide
 * y que tiene al menos un estado visible; null si no hay.
 */
export function findStatusSetForEntity<T extends StatusListItem = StatusListItem>(
  config: unknown,
  entity: unknown
): StatusSetEntry<T> | null {
  if (!asRecord(entity)) return null;
  const entries = getActiveStatusSetEntries<T>(config);
  if (entries.length === 0) return null;
  const current = normalizeStatusSetValue(readFieldPath(entity, getStatusBaseField(config)));
  if (current === '') return null;
  for (const entry of entries) {
    if (normalizeStatusSetValue(entry.value) === current && visibleSorted<T>(entry.statuses).length > 0) return entry;
  }
  return null;
}

/**
 * Lista de una clave: 'set:<id>' → esa lista (activa o no); 'AIR' | 'SEA' | 'ROA' → statusByMode[clave]
 * o, para AIR/SEA, el legado `status`. Solo visibles y ordenados; [] si no hay.
 */
export function statusListByKey<T extends StatusListItem = StatusListItem>(config: unknown, key: string): T[] {
  const record = asRecord(config);
  if (!record) return [];
  if (key.startsWith(STATUS_SET_KEY_PREFIX)) {
    const setId = key.slice(STATUS_SET_KEY_PREFIX.length);
    for (const entry of getStatusSetEntries<T>(config)) {
      if (entry.id === setId) return visibleSorted<T>(entry.statuses);
    }
    return [];
  }
  const byMode = asRecord(record.statusByMode);
  const modeList = byMode ? byMode[key] : undefined;
  if (Array.isArray(modeList)) return visibleSorted<T>(modeList);
  if ((key === 'AIR' || key === 'SEA') && Array.isArray(record.status)) return visibleSorted<T>(record.status);
  return [];
}

/** La regla: lista activa de la opción → statusByMode[modo] → legado (AIR/SEA) → null. */
export function resolveStatusList<T extends StatusListItem = StatusListItem>(
  config: unknown,
  entity: unknown
): ResolvedStatusList<T> | null {
  const record = asRecord(config);
  if (!record) return null;
  const entityRecord = asRecord(entity);
  const modeKey = mapTransportModeToKey(entityRecord ? entityRecord.transportMode : undefined);
  const set = findStatusSetForEntity<T>(config, entity);
  if (set) {
    return {
      key: STATUS_SET_KEY_PREFIX + set.id,
      source: 'set',
      setId: set.id,
      modeKey,
      statuses: visibleSorted<T>(set.statuses),
    };
  }
  const byMode = asRecord(record.statusByMode);
  if (byMode && Array.isArray(byMode[modeKey])) {
    return { key: modeKey, source: 'mode', setId: null, modeKey, statuses: visibleSorted<T>(byMode[modeKey]) };
  }
  if ((modeKey === 'AIR' || modeKey === 'SEA') && Array.isArray(record.status)) {
    return { key: modeKey, source: 'legacy', setId: null, modeKey, statuses: visibleSorted<T>(record.status) };
  }
  return null;
}

/** Clave de la lista del registro; sin lista, la clave de su modo. */
export function resolveStatusListKey(config: unknown, entity: unknown): string {
  const resolved = resolveStatusList(config, entity);
  if (resolved) return resolved.key;
  const entityRecord = asRecord(entity);
  return mapTransportModeToKey(entityRecord ? entityRecord.transportMode : undefined);
}

/** Orden de búsqueda: lista del registro, listas activas, AIR, SEA, ROA, legado y, al final, las inactivas. */
function allStatusLists<T extends StatusListItem>(config: unknown, entity: unknown): T[][] {
  const lists: T[][] = [];
  const seen: string[] = [];
  const push = (key: string, list: T[]) => {
    if (seen.indexOf(key) !== -1) return;
    seen.push(key);
    lists.push(list);
  };
  const resolved = resolveStatusList<T>(config, entity);
  if (resolved) push(resolved.key, resolved.statuses);
  for (const entry of getActiveStatusSetEntries<T>(config)) {
    push(STATUS_SET_KEY_PREFIX + entry.id, visibleSorted<T>(entry.statuses));
  }
  for (const mode of MODE_KEYS) push(mode, statusListByKey<T>(config, mode));
  const record = asRecord(config);
  push('legacy', visibleSorted<T>(record ? record.status : undefined));
  // Las activas ya están en `seen`: aquí solo entran las inactivas.
  for (const entry of getStatusSetEntries<T>(config)) {
    push(STATUS_SET_KEY_PREFIX + entry.id, visibleSorted<T>(entry.statuses));
  }
  return lists;
}

/** Primer estado visible con ese id, en el orden de búsqueda; null si no hay. */
export function findStatusInLists<T extends StatusListItem = StatusListItem>(
  config: unknown,
  entity: unknown,
  statusId: string
): T | null {
  for (const list of allStatusLists<T>(config, entity)) {
    for (const status of list) {
      if (status.id === statusId) return status;
    }
  }
  return null;
}

/** Primera etiqueta NO vacía del estado, en el orden de búsqueda; null si no hay. */
export function findStatusLabel(config: unknown, entity: unknown, statusId: string): string | null {
  for (const list of allStatusLists(config, entity)) {
    for (const status of list) {
      if (status.id === statusId && typeof status.label === 'string' && status.label !== '') return status.label;
    }
  }
  return null;
}

/** Primera lista (en el orden de búsqueda) que contiene TODOS los ids; null si ninguna. */
export function findListContainingAll<T extends StatusListItem = StatusListItem>(
  config: unknown,
  entity: unknown,
  statusIds: string[]
): T[] | null {
  for (const list of allStatusLists<T>(config, entity)) {
    let hasAll = true;
    for (const id of statusIds) {
      if (!list.some((status) => status.id === id)) {
        hasAll = false;
        break;
      }
    }
    if (hasAll) return list;
  }
  return null;
}

/** Todos los estados de las listas activas, sin filtrar por visible (para catálogos y filtros). */
export function allStatusSetStatuses<T extends StatusListItem = StatusListItem>(config: unknown): T[] {
  const result: T[] = [];
  for (const entry of getActiveStatusSetEntries<T>(config)) {
    for (const status of entry.statuses) {
      const record = asRecord(status);
      if (record && typeof record.id === 'string') result.push(status);
    }
  }
  return result;
}

/** Ruta del campo base activo para proyecciones select(); [] si el campo base es el modo de transporte. */
export function statusSetFieldPaths(config: unknown): string[] {
  const baseField = getStatusBaseField(config);
  return baseField === DEFAULT_STATUS_BASE_FIELD ? [] : [baseField];
}
